export type CalculationStatus =
  | 'DRAFT'
  | 'RUNNING'
  | 'UPLOADING'
  | 'UPLOAD_FAILED'
  | 'DONE'
  | 'ERROR'
  | 'TIMEOUT'
  | 'CANCELLED'

export type HardwareType = 'VIRTUAL_MACHINE'

export const DEFAULT_VM_MAX_RUNTIME_SECONDS = 86400

export const VM_MAX_RUNTIME_OPTIONS = [
  { value: 3600, label: '1 hour' },
  { value: 21600, label: '6 hours' },
  { value: 43200, label: '12 hours' },
  { value: 86400, label: '1 day' },
  { value: 172800, label: '2 days' },
  { value: 259200, label: '3 days' },
  { value: 604800, label: '7 days' }
] as const

export type VmMaxRuntimeSeconds = (typeof VM_MAX_RUNTIME_OPTIONS)[number]['value']

export type ContainerImageOption = {
  value: string
  label: string
  repository: string
  tag: string
}

export type HardwareProfile = {
  id: number
  name: string
  description?: string
  project?: number | null
  project_name?: string | null
  hardware_type: HardwareType
  hardware_type_display?: string | null
  spot_instance: boolean
  vm_size?: string
  vm_image?: string
  vm_os_disk_size_gb?: number
  vm_os_disk_storage_account_type?: string
  deployment_region?: string
  cpu_cores: number
  memory_gb: number
  created?: string
  modified?: string
}

export type HardwareProfileFormPayload = {
  id?: number
  name: string
  description?: string
  project?: number | null
  hardware_type: HardwareType
  spot_instance: boolean
  vm_size?: string
  vm_image?: string
  vm_os_disk_size_gb?: number
  vm_os_disk_storage_account_type?: string
  deployment_region?: string
  cpu_cores: number
  memory_gb: number
}

export type HardwareTypeOption = {
  value: HardwareType
  label: string
}

export type VirtualMachineSizeOption = {
  series: string
  size: string
  cpu_cores: number
  memory_gb: number
  regions?: string[]
}

export type AzureDeploymentRegionOption = {
  name: string
  label: string
}

export type AzureVmImageOption = {
  value: string
  label: string
  publisher: string
  offer: string
  sku: string
  version: string
}

export type AzureVmOsDiskStorageAccountTypeOption = {
  value: string
  label: string
  product_name: string
  sku_prefix: string
}

export type AzureVmOsDiskSizeOption = {
  size_gb: number
  sku_suffix: string
  sku_names: Record<string, string>
}

export type HardwareProfileOptions = {
  hardware_types: HardwareTypeOption[]
  vm_sizes: VirtualMachineSizeOption[]
  vm_images: AzureVmImageOption[]
  vm_os_disk_storage_account_types: AzureVmOsDiskStorageAccountTypeOption[]
  vm_os_disk_sizes: AzureVmOsDiskSizeOption[]
  deployment_regions: AzureDeploymentRegionOption[]
}

export type CalculationComputeResource = {
  name: string
  resource_type?: HardwareType | null
  historical?: boolean
  metrics_available?: boolean
  status?: string | null
  group_status?: string | null
  container_status?: string | null
  provisioning_state?: string | null
  detail_status?: string | null
  cpu_cores?: number | null
  memory_gb?: number | null
  cpu_usage_millicores?: number | null
  cpu_usage_cores?: number | null
  cpu_usage_percent?: number | null
  memory_usage_bytes?: number | null
  memory_usage_gb?: number | null
  memory_usage_percent?: number | null
  os_disk_size_gb?: number | null
  os_disk_storage_account_type?: string | null
  os_disk_usage_bytes?: number | null
  os_disk_usage_gb?: number | null
  os_disk_usage_percent?: number | null
  os_disk_free_bytes?: number | null
  os_disk_free_gb?: number | null
  spot_instance?: boolean | null
  price_per_hour?: number | null
  price_currency?: string | null
  price_unit?: string | null
  price_meter_name?: string | null
  price_product_name?: string | null
  price_effective_start_date?: string | null
  estimated_cost?: number | null
  compute_price_per_hour?: number | null
  compute_estimated_cost?: number | null
  os_disk_price_per_hour?: number | null
  os_disk_estimated_cost?: number | null
  os_disk_price_meter_name?: string | null
  os_disk_price_product_name?: string | null
  created?: string | null
  started?: string | null
  ended?: string | null
}

export type CalculationComputeResourceLogsResponse = {
  compute_resource_name: string
  container_name: string
  tail: number
  logs: string
}

export type CalculationInputUpload = {
  upload_id: string
  upload_url: string
  expires_in: number
}

export type Calculation = {
  id: number
  project: number
  is_demo?: boolean
  name: string
  note?: string | null
  zip_file_name?: string | null
  input_ready?: boolean
  input_auto_start?: boolean
  input_upload?: CalculationInputUpload | null
  input_upload_status?: 'EMPTY' | 'UPLOADING' | 'VALIDATING' | 'READY' | 'FAILED'
  input_upload_name?: string
  input_upload_error?: string
  container_image?: string | null
  vm_max_runtime_seconds: number
  estimated_compute_cost?: number | null
  estimated_monthly_storage_cost?: number | null
  cost_currency?: string | null
  hardware_profile: number | null
  hardware_profile_name?: string | null
  hardware_profile_type?: HardwareType | null
  hardware_profile_type_display?: string | null
  hardware_profile_cpu_cores?: number | null
  hardware_profile_memory_gb?: number | null
  created: string
  modified: string
  started?: string | null
  vm_runtime_deadline?: string | null
  ended?: string | null
  cancelled?: string | null
  favorite: boolean
  status: CalculationStatus | string
  task_id?: string | null
  compute_resource_name?: string | null
  compute_resource_status?: string | null
  [key: string]: unknown
}

export type CalculationFormPayload = {
  project: number
  name: string
  note?: string | null
  zipFile?: File | null
  hardwareProfile: number
  containerImage?: string | null
  vmMaxRuntimeSeconds: number
}

export type CalculationReschedulePayload = {
  containerImage: string
  hardwareProfile: number
  vmMaxRuntimeSeconds: number
}

export type CalculationResultFile = {
  name: string
  blob_name: string
  size?: number | null
  created?: string | null
  last_modified?: string | null
  content_type?: string | null
  download_url?: string | null
  estimated_monthly_storage_cost?: number | null
}

export type CalculationStorageCost = {
  total_size_bytes: number
  total_size_gb: number
  estimated_monthly_cost?: number | null
  currency_code?: string | null
  price_per_gb_month?: number | null
  unit_of_measure?: string | null
  meter_name?: string | null
  product_name?: string | null
  effective_start_date?: string | null
  region?: string | null
  sku_name?: string | null
}

export type CalculationResultFilesResponse = {
  prefix: string
  storage_cost?: CalculationStorageCost | null
  files: CalculationResultFile[]
}

export const OUTPUT_ACCESS_DURATIONS = [
  { value: 'hour', title: '1 hour' },
  { value: 'day', title: '1 day' },
  { value: 'week', title: '1 week' }
] as const

export type OutputAccessDuration = (typeof OUTPUT_ACCESS_DURATIONS)[number]['value']

export type OutputAccessLink = {
  access_url: string
  expires_at: string
  path: string
}
